import { Dialog } from '../../../components/dialog-portal/dialog.tsx';
import { ReportType, ReportTypes } from './reportSettings.ts';

interface Props {
    selectedReportType: ReportType;
    onClose: () => void;
}

const reportTypeInformation: Record<ReportType, {
    summary: string;
    details: string[];
}> = {
    Availability: {
        summary: 'Shows how much of the scheduled production time was spent producing.',
        details: [
            'Percentage is production time divided by production time plus stop time.',
            'Sum shows total production hours instead of the percentage.',
            'The target line is available for percentage reports.',
        ],
    },
    Scraps: {
        summary: 'Shows the scrap share of total output for the selected period.',
        details: [
            'The result is weighted by production time.',
            'It includes both potential and confirmed scraps.',
        ],
    },
    'Confirmed scrap ratio': {
        summary: 'Shows confirmed additional scrap as a share of recorded potential scrap.',
        details: [
            'A value of 0.20 means confirmed additional scrap equals 20% of the potential scrap amount.',
            'Items released or otherwise resolved without added confirmed scrap do not increase the ratio.',
        ],
    },
    'Potential scraps time spent': {
        summary: 'Shows the total time spent completing potential-scrap reviews.',
        details: [
            'Time is assigned to the period when the scrap-close form was completed.',
            'Bar values are displayed in hours.',
        ],
    },
    'Measurement counts': {
        summary: 'Shows how many scheduled production measurements were completed.',
        details: [
            'Setup measurements are excluded.',
            'Counts can be joined or separated by shift and filtered by shift slot and day type.',
        ],
    },
    'Measurement delays': {
        summary: 'Shows the distribution of completed measurements by how late they were.',
        details: [
            'Each bar totals 100%, with colours representing delay-duration ranges.',
            'A measurement appears in the period when it was completed, so unresolved measurements are not shown yet.',
        ],
    },
    'Shift delays': {
        summary: 'Shows unresolved measurement delay at the exact end of each completed shift.',
        details: [
            'Only measurements due during that shift and still unresolved at shift end are included.',
            'Bar height is the combined delay time; labels show shift team, total delay, and unresolved count.',
            'Click a bar to inspect the individual measurements.',
            'This report is recalculated from current records. A measurement that later disappears without a completion record may be missing from historical results.',
        ],
    },
    Setups: {
        summary: 'Shows total active setup hours for each worker across the selected date range.',
        details: [
            'The lower number is the amount of setups owned',
            'Time is assigned to the recorded setup owner.',
            'Disconnected machine time is excluded. Only time inside the date range is counted.',
            'Setups without a recorded owner or duration are excluded.',
        ],
    },
};

// Explains the report types and highlights the currently selected report.
export function ReportTypeInfoDialog({ selectedReportType, onClose }: Props) {
    return (
        <Dialog title='Report type information' onClose={onClose} large>
            <div className='report-graphs__info'>
                <div className='report-type-info'>
                    <p className='report-type-info__intro'>
                        Choose a report type to change what the graph measures. Date, shift, day, and display controls are applied where relevant.
                    </p>
                    {ReportTypes.map((reportType) => {
                        const information = reportTypeInformation[reportType];
                        return (
                            <section
                                key={reportType}
                                className={`report-type-info__item${reportType === selectedReportType ? ' selected' : ''}`}
                            >
                                <h3>{reportType}</h3>
                                <p>{information.summary}</p>
                                <ul>
                                    {information.details.map((detail) => <li key={detail}>{detail}</li>)}
                                </ul>
                            </section>
                        );
                    })}
                </div>
            </div>
        </Dialog>
    );
}
